// Blocos dos indicadores que dependem dos registros novos da #6509:
//   - erro e retrabalho do Faturamento (motivo do cancelamento do boleto em lista fechada);
//   - retenção (resultado do pedido de cancelamento);
//   - titularidade em até 3 dias úteis, com o registro do protocolo na Equatorial (Brenda);
//   - verba de mídia e custo de ferramentas contra o orçamento (Marketing e Felipe).
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Plus, X } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import { metaDoMes, type MetaMensal } from '../../src/lib/dashboard/metasMensais';
import { META_ERRO_RETRABALHO, erroERetrabalho } from '../../src/lib/motivosCancelamentoBoleto';
import { META_RETENCAO, retencaoDoPeriodo } from '../../src/lib/resultadoCancelamento';
import {
  DU_PRAZO_PROTOCOLO, META_TITULARIDADE_NO_PRAZO, contraOOrcamento, custoDasFerramentas, prazoDoProtocolo, titularidadeNoPrazo,
  type AreaDaFerramenta, type Ferramenta, type ProtocoloDaTroca,
} from '../../src/lib/visaoGeral/registrosFase3';
import type { TrocaAcompanhada } from '../../src/lib/visaoGeral/backoffice';
import { CartaoIndicador, PainelDetalhe, plural } from './componentes';
import { useCancelamentosDeBoleto, useCustosFerramentas, usePedidosEncerrados } from './useRegistrosFase3';

const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const reais0 = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataCurta = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
const CAMINHO_METAS = '/dashboard/painel-executivo/metas';

// ─────────────────────────── Erro e retrabalho ───────────────────────────

/** Cancelamentos de boleto do período, classificados, e a taxa sobre os boletos emitidos. */
export function useErroERetrabalho(inicio: Date, fim: Date, emitidos: number | null) {
  const cancelamentos = useCancelamentosDeBoleto(inicio.toISOString());
  return useMemo(() => {
    if (!cancelamentos.dados || emitidos == null) return null;
    const r = erroERetrabalho(cancelamentos.dados, inicio, fim);
    return { ...r, emitidos, taxa: emitidos && r.mensuravel ? r.erros / emitidos : null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelamentos.dados, emitidos, inicio.getTime(), fim.getTime()]);
}

export const CartaoErroERetrabalho: React.FC<{ e: ReturnType<typeof useErroERetrabalho>; rotuloMes: string; titulo?: string }> = ({ e, rotuloMes, titulo = 'Erro e retrabalho' }) => (
  <CartaoIndicador
    titulo={titulo}
    valor={e ? (e.taxa == null ? '—' : pct(e.taxa)) : null}
    meta={e?.taxa != null ? { texto: `meta: até ${pct(META_ERRO_RETRABALHO)}`, atingido: e.taxa <= 0 ? 1 : META_ERRO_RETRABALHO / e.taxa } : undefined}
    detalhe={e ? [
      e.mensuravel
        ? `${plural(e.erros, 'boleto cancelado', 'boletos cancelados')} por erro na emissão, de ${e.emitidos.toLocaleString('pt-BR')} emitidos em ${rotuloMes}.`
        : `Ainda sem medida: ${e.semMotivoDaLista} de ${plural(e.cancelamentos, 'cancelamento', 'cancelamentos')} de ${rotuloMes} têm motivo livre, de antes da lista de motivos (29/10/2026).`,
      e.mensuravel && e.semMotivoDaLista ? `${plural(e.semMotivoDaLista, 'cancelamento ficou', 'cancelamentos ficaram')} com motivo livre (antes da lista).` : null,
    ].filter(Boolean).join(' ') : undefined}
    composicao={e?.porMotivo.slice(0, 5).map(p => ({ rotulo: p.motivo.rotulo, valor: String(p.qtd), destaque: p.motivo.erroDoFaturamento }))}
    para="/financeiro"
    origem="Boletos cancelados com motivo de erro na emissão (dados do cliente, valor, código de barras, duplicidade, conta errada) ÷ boletos emitidos no mês (1ª emissão de cada fatura). O motivo virou lista fechada em 29/10/2026; antes era texto livre e não entra."
  />
);

// ─────────────────────────── Retenção ───────────────────────────

export const CartaoRetencao: React.FC<{ inicio: Date; fim: Date; rotuloMes: string }> = ({ inicio, fim, rotuloMes }) => {
  const pedidos = usePedidosEncerrados(inicio.toISOString());
  const r = useMemo(() => (pedidos.dados ? retencaoDoPeriodo(pedidos.dados, inicio, fim) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pedidos.dados, inicio.getTime(), fim.getTime()]);
  return (
    <CartaoIndicador
      titulo="Retenção"
      valor={r ? (r.taxa == null ? '—' : pct(r.taxa)) : null}
      meta={r?.taxa != null ? { texto: `meta ${pct(META_RETENCAO)}`, atingido: r.taxa / META_RETENCAO } : undefined}
      detalhe={r ? (r.encerrados
        ? `${r.ficaram} de ${plural(r.encerrados, 'pedido de cancelamento encerrado', 'pedidos de cancelamento encerrados')} em ${rotuloMes} terminaram com o cliente ficando.`
        : `Nenhum pedido de cancelamento encerrado em ${rotuloMes}.`) : undefined}
      composicao={r?.porMotivo.map(p => ({ rotulo: p.motivo?.rotulo ?? 'Motivo livre (antes da lista)', valor: String(p.qtd) }))}
      para="/financeiro/cancelamentos"
      origem="Pedidos encerrados no mês: desistência (o cliente ficou) ÷ concluídos + desistências. O porquê da desistência virou lista fechada em 29/10/2026. A planilha importada fica de fora: ela não trouxe as desistências."
    />
  );
};

// ─────────────────────────── Titularidade ───────────────────────────

export const CartaoTitularidadeNoPrazo: React.FC<{
  assinadas: TrocaAcompanhada[] | null;
  protocolos: { porTermo: Map<number, ProtocoloDaTroca>; semMigration: boolean } | null;
  inicio: Date; fim: Date; hojeIso: string; rotuloMes: string;
}> = ({ assinadas, protocolos, inicio, fim, hojeIso, rotuloMes }) => {
  const r = useMemo(() => (assinadas && protocolos
    ? titularidadeNoPrazo(assinadas.map(a => ({ assinado_em: a.troca.assinado_em, protocolo: protocolos.porTermo.get(a.troca.id) ?? null })), inicio, fim, hojeIso)
    : null), [assinadas, protocolos, inicio, fim, hojeIso]);
  return (
    <CartaoIndicador
      titulo={`Titularidade em até ${DU_PRAZO_PROTOCOLO} dias úteis`}
      valor={protocolos?.semMigration ? '—' : r ? (r.taxa == null ? '—' : pct(r.taxa)) : null}
      meta={r?.taxa != null ? { texto: `meta ${pct(META_TITULARIDADE_NO_PRAZO)}`, atingido: r.taxa / META_TITULARIDADE_NO_PRAZO } : undefined}
      detalhe={protocolos?.semMigration ? 'O registro do protocolo ainda não está disponível (falta aplicar a atualização do banco).'
        : r ? (r.assinadas
          ? `${r.noPrazo} de ${r.noPrazo + r.foraDoPrazo} trocas assinadas em ${rotuloMes} foram protocoladas na Equatorial no prazo.${r.aguardando ? ` ${plural(r.aguardando, 'ainda está', 'ainda estão')} dentro do prazo.` : ''}`
          : `Nenhuma troca assinada em ${rotuloMes}.`) : undefined}
      origem="Do contrato assinado ao protocolo da troca na Equatorial, em dias úteis (com feriados). Troca sem protocolo conta como fora do prazo depois do 3º dia útil. O protocolo é registrado na lista de titularidades pendentes."
    />
  );
};

/** Janela para registrar o protocolo da troca e o da transferência de saldo. */
export const ModalProtocoloTitularidade: React.FC<{
  troca: TrocaAcompanhada;
  protocolo: ProtocoloDaTroca | null;
  aoFechar: () => void;
  aoSalvar: () => void;
}> = ({ troca, protocolo, aoFechar, aoSalvar }) => {
  const { profile } = useAuth();
  const hoje = new Date();
  const hojeIso = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
  const [f, setF] = useState({
    protocolo: protocolo?.protocolo_titularidade ?? '',
    data: protocolo?.titularidade_protocolada_em?.slice(0, 10) ?? hojeIso,
    saldo: protocolo?.protocolo_saldo ?? '',
    dataSaldo: protocolo?.saldo_protocolado_em?.slice(0, 10) ?? '',
    semSaldo: protocolo?.saldo_nao_se_aplica ?? false,
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const prazo = troca.troca.assinado_em ? prazoDoProtocolo(troca.troca.assinado_em) : null;

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !salvando) aoFechar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar, salvando]);

  const salvar = async () => {
    if (!f.protocolo.trim() || !f.data) { setErro('Informe o número do protocolo da troca e o dia em que foi aberto.'); return; }
    if (f.data > hojeIso) { setErro('O dia do protocolo não pode ser no futuro.'); return; }
    if (!f.semSaldo && f.saldo.trim() && !f.dataSaldo) { setErro('Informe o dia do protocolo da transferência de saldo.'); return; }
    setSalvando(true); setErro(null);
    const { error } = await supabase.from('termo_troca_titularidade_logs').update({
      protocolo_titularidade: f.protocolo.trim(),
      titularidade_protocolada_em: f.data,
      protocolo_saldo: f.semSaldo ? null : (f.saldo.trim() || null),
      saldo_protocolado_em: f.semSaldo || !f.saldo.trim() ? null : f.dataSaldo,
      saldo_nao_se_aplica: f.semSaldo,
      protocolo_registrado_por: profile?.id ?? null,
      protocolo_registrado_em: new Date().toISOString(),
    }).eq('id', troca.troca.id);
    setSalvando(false);
    if (error) { setErro(`Não foi possível salvar: ${error.message}`); return; }
    aoSalvar();
  };

  const campo = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50';
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="titulo-protocolo"
      onClick={() => { if (!salvando) aoFechar(); }}>
      <div className="w-full sm:max-w-md bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl p-5 shadow-xl space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 id="titulo-protocolo" className="font-poppins font-bold text-gray-900 dark:text-white">Protocolo na Equatorial</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {troca.troca.cliente_nome || troca.negociacao?.titulo || 'Sem nome'} · UC {troca.troca.uc_numero || '—'}
            </p>
            {prazo && <p className="text-xs text-gray-500 dark:text-gray-400">Assinada em {dataCurta(troca.troca.assinado_em)}; prazo para protocolar: {dataCurta(prazo)}.</p>}
          </div>
          <button type="button" onClick={aoFechar} disabled={salvando} aria-label="Fechar" className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_9rem] gap-3">
          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">Protocolo da troca *
            <input className={`${campo} mt-1`} value={f.protocolo} onChange={e => setF({ ...f, protocolo: e.target.value })} placeholder="Número do protocolo" />
          </label>
          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">Aberto em *
            <input type="date" className={`${campo} mt-1`} value={f.data} max={hojeIso} onChange={e => setF({ ...f, data: e.target.value })} />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
          <input type="checkbox" checked={f.semSaldo} onChange={e => setF({ ...f, semSaldo: e.target.checked })} className="rounded" />
          Sem saldo para transferir
        </label>
        {!f.semSaldo && (
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_9rem] gap-3">
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">Protocolo da transferência de saldo
              <input className={`${campo} mt-1`} value={f.saldo} onChange={e => setF({ ...f, saldo: e.target.value })} placeholder="Se já foi aberto" />
            </label>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">Aberto em
              <input type="date" className={`${campo} mt-1`} value={f.dataSaldo} max={hojeIso} onChange={e => setF({ ...f, dataSaldo: e.target.value })} />
            </label>
          </div>
        )}
        {erro && <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={aoFechar} disabled={salvando} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-600 dark:text-gray-300">Cancelar</button>
          <button type="button" onClick={salvar} disabled={salvando} className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-60">{salvando ? 'Salvando…' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────── Verba de mídia ───────────────────────────

/** Gasto de mídia contra a verba do mês (aba Metas, indicador `mkt_investimento`). */
export function verbaDeMidia(metas: readonly MetaMensal[], mes: string, gasto: number, fracao: number) {
  const verba = metaDoMes(metas, 'mkt_investimento', mes);
  return { ...contraOOrcamento(gasto, verba?.valor ?? null, fracao), verbaHerdada: verba?.herdada ?? false };
}

/** A meta do cartão de investimento: barra cheia e verde enquanto a projeção cabe na verba. */
export function metaDaVerba(v: ReturnType<typeof verbaDeMidia>) {
  if (v.orcamento == null) return undefined;
  return {
    texto: `verba ${reais0(v.orcamento)} · ${pct(v.uso ?? 0)} usado${v.projecao !== v.gasto ? `; no ritmo, fecha em ${reais0(v.projecao)}` : ''}`,
    atingido: v.estourou ? v.orcamento / v.projecao : 1,
  };
}

// ─────────────────────────── Ferramentas e mídia (Felipe) ───────────────────────────

const AREA: Record<AreaDaFerramenta, string> = { tecnologia: 'Tecnologia', marketing: 'Marketing' };
type Rascunho = { id: number | null; nome: string; area: AreaDaFerramenta; valor: string; inicio: string; fim: string; observacao: string };
const lerValor = (t: string) => { const v = Number(t.replace(/\./g, '').replace(',', '.')); return Number.isFinite(v) && v >= 0 ? v : null; };

export const PainelCustoFerramentasEMidia: React.FC<{
  mes: string; rotuloMes: string; gastoMidia: number | null; fracao: number; metas: readonly MetaMensal[];
}> = ({ mes, rotuloMes, gastoMidia, fracao, metas }) => {
  const { profile } = useAuth();
  const [versao, setVersao] = useState(0);
  const custos = useCustosFerramentas(versao);
  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const doMes = useMemo(() => (custos.dados ? custoDasFerramentas(custos.dados.ferramentas, mes) : null), [custos.dados, mes]);
  const orcamento = metaDoMes(metas, 'tec_custo_ferramentas', mes);
  const ferr = doMes ? contraOOrcamento(doMes.total, orcamento?.valor ?? null, fracao, { projetar: false }) : null;
  const midia = gastoMidia != null ? verbaDeMidia(metas, mes, gastoMidia, fracao) : null;
  const ehAdm = profile?.role === 'adm';
  // Total: ferramentas (custo fixo) + mídia; a projeção só vale para a mídia.
  const total = doMes && midia && ferr?.orcamento != null && midia.orcamento != null
    ? (() => {
        const gasto = doMes.total + midia.gasto;
        const orc = ferr.orcamento + midia.orcamento;
        const projecao = doMes.total + midia.projecao;
        return { gasto, orcamento: orc, uso: gasto / orc, projecao, estourou: projecao > orc };
      })()
    : null;

  const salvar = async () => {
    if (!rascunho) return;
    const valor = lerValor(rascunho.valor);
    if (!rascunho.nome.trim() || valor == null || !rascunho.inicio) { setErro('Informe nome, valor mensal e o mês de início.'); return; }
    if (rascunho.fim && rascunho.fim < rascunho.inicio) { setErro('O último mês não pode ser antes do início.'); return; }
    setSalvando(true); setErro(null);
    const linha = {
      nome: rascunho.nome.trim(), area: rascunho.area, valor_mensal: valor,
      inicio: `${rascunho.inicio.slice(0, 7)}-01`, fim: rascunho.fim ? `${rascunho.fim.slice(0, 7)}-01` : null,
      observacao: rascunho.observacao.trim() || null, atualizado_por: profile?.id ?? null, atualizado_em: new Date().toISOString(),
    };
    const { error } = rascunho.id == null
      ? await supabase.from('custos_ferramentas').insert(linha)
      : await supabase.from('custos_ferramentas').update(linha).eq('id', rascunho.id);
    setSalvando(false);
    if (error) { setErro(`Não foi possível salvar: ${error.message}`); return; }
    setRascunho(null);
    setVersao(v => v + 1);
  };

  const editar = (f: Ferramenta | null) => {
    setErro(null);
    setRascunho(f
      ? { id: f.id, nome: f.nome, area: f.area, valor: String(Number(f.valor_mensal)).replace('.', ','), inicio: f.inicio.slice(0, 7), fim: f.fim ? f.fim.slice(0, 7) : '', observacao: f.observacao ?? '' }
      : { id: null, nome: '', area: 'tecnologia', valor: '', inicio: mes.slice(0, 7), fim: '', observacao: '' });
  };

  const campo = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50';
  const linhaResumo = (rotulo: string, gasto: number | null, o: { orcamento: number | null; uso: number | null; estourou: boolean; projecao: number; gasto: number } | null, extra?: string) => (
    <div className="rounded-xl bg-gray-50 dark:bg-gray-900/40 p-3 min-w-0">
      <span className="block text-xs text-gray-500 dark:text-gray-400">{rotulo}</span>
      <span className="block text-lg font-bold text-gray-900 dark:text-white tabular-nums">{gasto == null ? '…' : reais0(gasto)}</span>
      <span className={`block text-xs ${o?.estourou ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
        {o?.orcamento != null
          ? `de ${reais0(o.orcamento)} (${pct(o.uso ?? 0)})${o.projecao !== o.gasto ? `; no ritmo, ${reais0(o.projecao)}` : ''}`
          : 'sem orçamento definido'}
        {extra ? ` ${extra}` : ''}
      </span>
    </div>
  );

  return (
    <PainelDetalhe titulo="Ferramentas e mídia contra o orçamento" acao={{ rotulo: 'Definir orçamento', para: CAMINHO_METAS }}>
      {custos.erro ? (
        <p className="text-sm text-red-600 dark:text-red-400">Não foi possível carregar as ferramentas.</p>
      ) : custos.dados?.semMigration ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">O cadastro de ferramentas ainda não está disponível: falta aplicar a atualização do banco da #6509.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
            {linhaResumo(`Ferramentas em ${rotuloMes}`, doMes?.total ?? null, ferr)}
            {linhaResumo(`Mídia em ${rotuloMes}`, gastoMidia, midia)}
            {linhaResumo('Total', doMes && gastoMidia != null ? doMes.total + gastoMidia : null, total)}
          </div>
          {!doMes ? (
            <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : doMes.ferramentas.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma ferramenta cadastrada para {rotuloMes}.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {doMes.ferramentas.map(f => (
                <li key={f.id} className="flex items-center gap-3 py-2 text-sm">
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-gray-800 dark:text-gray-100">{f.nome}</span>
                    <span className="block text-xs text-gray-400 truncate">{AREA[f.area]}{f.fim ? ` · até ${dataCurta(f.fim)}/${f.fim.slice(0, 4)}` : ''}{f.observacao ? ` · ${f.observacao}` : ''}</span>
                  </span>
                  <span className="tabular-nums font-semibold text-gray-900 dark:text-white whitespace-nowrap">{reais(f.valor)}</span>
                  {ehAdm && (
                    <button type="button" onClick={() => editar(f)} aria-label={`Editar ${f.nome}`} className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10"><Pencil size={14} /></button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {ehAdm && !rascunho && (
            <button type="button" onClick={() => editar(null)} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
              <Plus size={14} /> Cadastrar ferramenta
            </button>
          )}
          {rascunho && (
            <div className="mt-3 rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_9rem_8rem] gap-3">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">Ferramenta
                  <input className={`${campo} mt-1`} value={rascunho.nome} onChange={e => setRascunho({ ...rascunho, nome: e.target.value })} placeholder="Ex.: Supabase" />
                </label>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">Área
                  <select className={`${campo} mt-1`} value={rascunho.area} onChange={e => setRascunho({ ...rascunho, area: e.target.value as AreaDaFerramenta })}>
                    <option value="tecnologia">Tecnologia</option>
                    <option value="marketing">Marketing</option>
                  </select>
                </label>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">R$ por mês
                  <input className={`${campo} mt-1`} inputMode="decimal" value={rascunho.valor} onChange={e => setRascunho({ ...rascunho, valor: e.target.value })} placeholder="0,00" />
                </label>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-[9rem_9rem_1fr] gap-3">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">Desde
                  <input type="month" className={`${campo} mt-1`} value={rascunho.inicio} onChange={e => setRascunho({ ...rascunho, inicio: e.target.value })} />
                </label>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">Último mês
                  <input type="month" className={`${campo} mt-1`} value={rascunho.fim} onChange={e => setRascunho({ ...rascunho, fim: e.target.value })} />
                </label>
                <label className="col-span-2 sm:col-span-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">Observação
                  <input className={`${campo} mt-1`} value={rascunho.observacao} onChange={e => setRascunho({ ...rascunho, observacao: e.target.value })} placeholder="Ex.: US$ 25, cobrança anual ÷ 12" />
                </label>
              </div>
              {erro && <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>}
              <div className="flex flex-wrap justify-end gap-2">
                {rascunho.id != null && !rascunho.fim && (
                  <button type="button" disabled={salvando} onClick={() => setRascunho({ ...rascunho, fim: mes.slice(0, 7) })}
                    className="mr-auto px-3 py-2 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">Parar de pagar a partir de {rotuloMes}</button>
                )}
                <button type="button" onClick={() => { setRascunho(null); setErro(null); }} disabled={salvando} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-600 dark:text-gray-300">Cancelar</button>
                <button type="button" onClick={salvar} disabled={salvando} className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-60">{salvando ? 'Salvando…' : 'Salvar'}</button>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400 mt-3">
            Valor em reais, como sai na fatura do cartão (cobrança anual: dividir por 12). Mídia = gasto no Meta Ads, com a projeção do mês.
            {' '}O orçamento das ferramentas e a verba de mídia são definidos mês a mês na <Link to={CAMINHO_METAS} className="text-primary hover:underline">aba Metas</Link> do Painel Executivo
            {orcamento?.herdada || midia?.verbaHerdada ? ' (sem valor novo no mês, vale o último definido)' : ''}.
          </p>
        </>
      )}
    </PainelDetalhe>
  );
};
